import fs from "node:fs";
import path from "node:path";
import { Config } from "@interpreter/config/config";
import { ColorManager } from "@interpreter/config/managers/color/manager";
import { Interpreter, type InterpreterResult } from "@interpreter/interpreter";
import { parseExpression } from "@interpreter/parser";
import { BaseSymbolType, ColorSymbol, NumberWithUnitSymbol } from "@interpreter/symbols";
import { groupBy } from "./interpreter/utils/coll";
import { isArray, isObject } from "./interpreter/utils/type";
import { InterpreterError, type ISymbolType, LexerError, ParserError } from "./lib";

interface TestCase {
  name: string;
  input: string;
  expectedOutput?: any;
  expectedOutputType: string;
  expectedErrorCode?: string;
  inline: boolean;
  context?: Record<string, any>;
  schemas?: string[];
  path: string;
}

interface TestResult extends TestCase {
  status: "passed" | "failed";
  actualOutput: any;
  actualOutputType: string;
  actualErrorCode?: string;
  error?: Error;
}

interface ComplianceReport {
  passed: number;
  failed: number;
  results: TestResult[];
}

const SCHEMA_FILE_MAP: Record<string, string> = {
  "https://schema.tokenscript.dev.gcp.tokens.studio/api/v1/schema/hsl-color/0/":
    "./data/specifications/colors/hsl.json",
  "https://schema.tokenscript.dev.gcp.tokens.studio/api/v1/schema/srgb-color/0/":
    "./data/specifications/colors/srgb.json",
  "https://schema.tokenscript.dev.gcp.tokens.studio/api/v1/schema/rgb-color/0/":
    "./data/specifications/colors/rgb.json",
  "https://schema.tokenscript.dev.gcp.tokens.studio/api/v1/schema/lrgb-color/0/":
    "./data/specifications/colors/lrgb.json",
};

function loadSchemas(schemas: string[]): ColorManager {
  const colorManager = new ColorManager();

  for (const schemaUri of schemas) {
    const filePath = SCHEMA_FILE_MAP[schemaUri];
    if (filePath && fs.existsSync(filePath)) {
      try {
        const specData = fs.readFileSync(filePath, "utf-8");
        colorManager.register(schemaUri, specData);
      } catch (error) {
        console.warn(`Failed to load schema ${schemaUri} from ${filePath}:`, error);
      }
    } else {
      console.warn(`No file mapping found for schema: ${schemaUri}`);
    }
  }

  return colorManager;
}

function readJsonFilesRecursively(dir: string): string[] {
  let results: string[] = [];
  const list = fs.readdirSync(dir);
  list.forEach((file) => {
    const filePath = path.join(dir, file);
    const stat = fs.statSync(filePath);
    if (stat?.isDirectory()) {
      results = results.concat(readJsonFilesRecursively(filePath));
    } else if (file.endsWith(".json")) {
      results.push(filePath);
    }
  });
  return results;
}

interface ComplianceConfig {
  dir?: string;
  file?: string;
  output?: string;
}

const parseTestCasesJson = (json: string, filePath: string): TestCase[] => {
  try {
    const testCases = JSON.parse(json);
    return !Array.isArray(testCases) ? [testCases] : testCases;
  } catch (_e) {
    throw new Error(`Could not parse json at path: ${filePath}`);
  }
};

function normalizeContextObject(
  obj: any,
  config: Config,
  fns: Array<(obj: any, config: Config) => any>,
): any {
  for (const rule of fns) {
    const transformed = rule(obj, config);
    if (typeof transformed !== "undefined") {
      return transformed;
    }
  }
  if (isArray(obj)) {
    return obj.map((el) => normalizeContextObject(el, config, fns));
  }
  if (isObject(obj)) {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(obj)) {
      out[k] = normalizeContextObject(v, config, fns);
    }
    return out;
  }
  return obj;
}

function transformContextToSymbols(obj: any, config: Config): any {
  return normalizeContextObject(obj, config, [
    (val: any, config: Config) => {
      if (isObject(val) && val.type === "NumberWithUnit") {
        const numberVal = val as unknown as NumberWithUnitSymbol;
        return new NumberWithUnitSymbol(numberVal.value ?? null, numberVal.unit, config);
      }
    },
  ]);
}

const runTest = (test: TestCase): { interpreter: Interpreter; result: InterpreterResult } => {
  const { ast } = parseExpression(test.input, { mode: test.inline ? "inline" : "script" });

  let config: Config;
  if (test.schemas && test.schemas.length > 0) {
    const colorManager = loadSchemas(test.schemas);
    config = new Config({ colorManager });
  } else {
    config = new Config();
  }

  const references = test.context ? transformContextToSymbols(test.context, config) : {};
  const interpreter = new Interpreter(ast, { references, config });
  return {
    interpreter,
    result: interpreter.interpret(),
  };
};

interface ComplianceResult {
  actualOutput: string;
  actualOutputType: string;
  actualErrorCode?: string;
}

const interpreterSymbolToComplianceResult = (
  symbol: ISymbolType,
  interpreter: Interpreter,
): ComplianceResult => {
  // Colors need to be formatted using ColorManager, as we need the order rules
  if (symbol instanceof ColorSymbol) {
    return {
      actualOutput: interpreter.config.colorManager.formatColorMethod(symbol),
      actualOutputType: symbol.getTypeName(),
    };
  }

  return {
    actualOutput: symbol.toString(),
    actualOutputType: symbol.getTypeName(),
  };
};

const compareResults = (
  complianceResult: ComplianceResult,
  testCase: TestCase,
): "passed" | "failed" => {
  const typeMatches = complianceResult.actualOutputType === testCase.expectedOutputType;

  // When expectedErrorCode is set, match on error code (and type) only
  if (testCase.expectedErrorCode) {
    return typeMatches && complianceResult.actualErrorCode === testCase.expectedErrorCode
      ? "passed"
      : "failed";
  }

  // Otherwise match on output value and type
  return typeMatches && complianceResult.actualOutput === testCase.expectedOutput
    ? "passed"
    : "failed";
};

export async function evaluateStandardCompliance(config: ComplianceConfig) {
  const files = config.file
    ? [config.file]
    : config.dir
      ? readJsonFilesRecursively(config.dir)
      : [];

  const testResults: TestResult[] = files.flatMap((path) => {
    const content = fs.readFileSync(path, "utf-8");
    const testCases: TestCase[] = parseTestCasesJson(content, path);

    return testCases.map((testCase) => {
      let testResult: TestResult;
      try {
        const { result, interpreter } = runTest(testCase);
        if (!(result instanceof BaseSymbolType)) {
          throw new Error(`result (${result}) in s not interpreter Symbol`);
        }
        const complianceResult = interpreterSymbolToComplianceResult(result, interpreter);
        testResult = {
          ...testCase,
          ...complianceResult,
          path: path,
          status: compareResults(complianceResult, testCase),
        };
      } catch (e: any) {
        if (e instanceof InterpreterError || e instanceof LexerError || e instanceof ParserError) {
          const complianceResult: ComplianceResult = {
            actualOutput: e.originalMessage || "",
            actualOutputType: "Error",
            actualErrorCode: e.code,
          };
          return {
            ...testCase,
            ...complianceResult,
            path: path,
            status: compareResults(complianceResult, testCase),
            error: e,
          };
        }
        throw e;
      }

      return {
        ...testCase,
        ...testResult,
        status: "passed",
        path: path,
      };
    });
  });

  const { passed = [], failed = [] } = groupBy((x) => x.status, testResults);

  // Order keys for json output
  const orderedResults = testResults.map(
    ({
      name,
      input,
      inline,
      context,
      path,
      status,
      actualOutput,
      actualOutputType,
      actualErrorCode,
      expectedOutput,
      expectedOutputType,
      expectedErrorCode,
      ...rest
    }) => ({
      name,
      path,
      status,
      inline,
      context,
      input,
      actualOutput,
      actualOutputType,
      ...(actualErrorCode ? { actualErrorCode } : {}),
      expectedOutput,
      expectedOutputType,
      ...(expectedErrorCode ? { expectedErrorCode } : {}),
      ...rest,
    }),
  );

  const report: ComplianceReport = {
    passed: passed.length,
    failed: failed.length,
    results: orderedResults,
  };

  if (config.output) {
    fs.writeFileSync(config.output, JSON.stringify(report, null, 2), "utf-8");
  }

  return report;
}
